/**
 * Vocabulario do modulo de modelos.
 *
 * Tudo que e rotulo, tom, limite ou momento mora aqui, e nao espalhado nos
 * componentes: a grade, a tabela, a previa, o editor e a comparacao precisam
 * dizer "Texto livre (Evolution)" e "Carrinho abandonado · T+1h" com as mesmas
 * palavras, e os limites da Meta valem tanto no contador quanto na checagem
 * (validacao.ts).
 *
 * Records com chave de uniao literal: se `types.ts` ganhar um canal, status ou
 * publico, o tsc aponta o Record incompleto em vez de renderizar `undefined`.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatCount } from '../../lib/format';
import type {
  ButtonKind,
  MessageTemplate,
  TemplateAudience,
  TemplateCategory,
  TemplateChannel,
  TemplatePerformance,
  TemplateSector,
  TemplateStatus,
  TemplateTab,
} from './types';

// -----------------------------------------------------------------------------
// Canal
// -----------------------------------------------------------------------------

export const CANAIS: readonly TemplateChannel[] = ['whatsapp_oficial', 'whatsapp_evolution', 'email', 'voice'];

export const CANAL_LABEL: Readonly<Record<TemplateChannel, string>> = {
  whatsapp_oficial: 'WhatsApp · API oficial (Meta)',
  whatsapp_evolution: 'WhatsApp · Evolution (texto livre)',
  email: 'E-mail',
  voice: 'Voz · roteiro da IA',
};

/** Rotulo curto para coluna de tabela e cabecalho de comparacao. */
export const CANAL_CURTO: Readonly<Record<TemplateChannel, string>> = {
  whatsapp_oficial: 'WhatsApp oficial',
  whatsapp_evolution: 'WhatsApp Evolution',
  email: 'E-mail',
  voice: 'Voz',
};

/** Por onde a mensagem sai de fato. */
export const SAIDA_LABEL: Readonly<Record<TemplateChannel, string>> = {
  whatsapp_oficial: 'API oficial Meta',
  whatsapp_evolution: 'Evolution API',
  email: 'E-mail transacional',
  voice: 'Ligação da IA (Vapi)',
};

export const CANAL_ICONE: Readonly<Record<TemplateChannel, IconName>> = {
  whatsapp_oficial: 'whatsapp',
  whatsapp_evolution: 'whatsapp',
  email: 'email',
  voice: 'telefone',
};

export const ABAS: readonly TemplateTab[] = ['whatsapp', 'email', 'voice'];

export const ABA_LABEL: Readonly<Record<TemplateTab, string>> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  voice: 'Voz (roteiro da IA)',
};

export const ABA_ICONE: Readonly<Record<TemplateTab, IconName>> = {
  whatsapp: 'whatsapp',
  email: 'email',
  voice: 'telefone',
};

export function abaDoCanal(channel: TemplateChannel): TemplateTab {
  switch (channel) {
    case 'whatsapp_oficial':
    case 'whatsapp_evolution':
      return 'whatsapp';
    case 'email':
      return 'email';
    case 'voice':
      return 'voice';
  }
}

/** So a API oficial tem botoes de WhatsApp; e-mail tem um botao de acao proprio. */
export function canalTemBotoes(channel: TemplateChannel): boolean {
  return channel === 'whatsapp_oficial';
}

export function canalPassaPelaMeta(channel: TemplateChannel): boolean {
  return channel === 'whatsapp_oficial';
}

export function canalTemRodape(channel: TemplateChannel): boolean {
  return channel === 'whatsapp_oficial' || channel === 'whatsapp_evolution';
}

// -----------------------------------------------------------------------------
// Publico e momento: SO depois do fato
// -----------------------------------------------------------------------------

export const PUBLICOS: readonly TemplateAudience[] = ['checkout_abandonado', 'plano_vencido', 'respondeu_regua', 'pagou_apos_toque'];

export const PUBLICO_LABEL: Readonly<Record<TemplateAudience, string>> = {
  checkout_abandonado: 'Carrinho abandonado',
  plano_vencido: 'Plano vencido',
  respondeu_regua: 'Respondeu à régua',
  pagou_apos_toque: 'Pagou após um toque',
};

/** Frase de negocio: com quem o modelo fala, sempre a partir de um fato ja ocorrido. */
export const PUBLICO_DESCRICAO: Readonly<Record<TemplateAudience, string>> = {
  checkout_abandonado: 'Quem abriu o checkout e ficou 30 min sem pagar.',
  plano_vencido: 'Quem teve a assinatura vencida e não renovou.',
  respondeu_regua: 'Quem respondeu a um toque e está com a vendedora do comercial.',
  pagou_apos_toque: 'Quem acabou de pagar depois de um toque (acesso liberado, recibo).',
};

export const PUBLICO_ICONE: Readonly<Record<TemplateAudience, IconName>> = {
  checkout_abandonado: 'carrinho',
  plano_vencido: 'calendario',
  respondeu_regua: 'resposta',
  pagou_apos_toque: 'check',
};

/**
 * Momentos permitidos por publico, todos contados DEPOIS do fato: T+ e depois
 * do abandono (que ja e 30 min sem pagar), D+ e depois do vencimento. Nao ha
 * D-: lembrete antes de vencer nao gera recuperacao e nao gera comissao.
 */
export const MOMENTOS: Readonly<Record<TemplateAudience, readonly string[]>> = {
  checkout_abandonado: ['T+30min', 'T+1h', 'T+24h', 'T+48h'],
  plano_vencido: ['D+15', 'D+30', 'D+60', 'D+90'],
  respondeu_regua: ['Após a resposta', 'Resposta sem venda'],
  pagou_apos_toque: ['Após o pagamento'],
};

export function descreverPublico(template: Pick<MessageTemplate, 'audience' | 'touch'>): string {
  return template.touch === null ? `${PUBLICO_LABEL[template.audience]} · disparo avulso` : `${PUBLICO_LABEL[template.audience]} · ${template.touch}`;
}

// -----------------------------------------------------------------------------
// Categoria, setor, status
// -----------------------------------------------------------------------------

export const CATEGORIAS: readonly TemplateCategory[] = ['utility', 'marketing'];

export const CATEGORIA_LABEL: Readonly<Record<TemplateCategory, string>> = {
  utility: 'Utility',
  marketing: 'Marketing',
};

export const CATEGORIA_TOM: Readonly<Record<TemplateCategory, BadgeTone>> = {
  utility: 'info',
  marketing: 'accent',
};

export const CATEGORIA_DICA: Readonly<Record<TemplateCategory, string>> = {
  utility: 'Transacional: fala de algo que a pessoa já iniciou (carrinho parado, acesso vencido, PIX expirado). É o que a régua usa.',
  marketing: 'Promocional: turma nova, condição da empresa. Exige opt-in e passa por análise mais rígida da Meta.',
};

export const SETORES: readonly TemplateSector[] = ['funil', 'comercial', 'suporte'];

export const SETOR_LABEL: Readonly<Record<TemplateSector, string>> = {
  funil: 'Funil',
  comercial: 'Comercial',
  suporte: 'Suporte',
};

export const STATUS_LISTA: readonly TemplateStatus[] = ['aprovado', 'pendente', 'rejeitado', 'texto_livre'];

export const STATUS_TOM: Readonly<Record<TemplateStatus, BadgeTone>> = {
  aprovado: 'good',
  pendente: 'warning',
  rejeitado: 'critical',
  texto_livre: 'info',
};

/** Rotulo curto para o chip de filtro (sem canal). */
export const STATUS_LABEL_CURTO: Readonly<Record<TemplateStatus, string>> = {
  aprovado: 'Aprovado',
  pendente: 'Pendente',
  rejeitado: 'Rejeitado',
  texto_livre: 'Texto livre',
};

/** Rotulo do selo no cartao: "texto livre" ganha o canal, porque e o canal que explica por que nao ha aprovacao. */
export function statusLabel(status: TemplateStatus, channel: TemplateChannel): string {
  if (status !== 'texto_livre') {
    return STATUS_LABEL_CURTO[status];
  }
  switch (channel) {
    case 'whatsapp_evolution':
      return 'Texto livre (Evolution)';
    case 'email':
      return 'Texto livre (e-mail)';
    case 'voice':
      return 'Roteiro da IA';
    case 'whatsapp_oficial':
      return 'Texto livre';
  }
}

/** O que acontece quando o modelo esta nesse status, em termos de envio. */
export const STATUS_DICA: Readonly<Record<TemplateStatus, string>> = {
  aprovado: 'Aprovado pela Meta: pode sair a qualquer hora, inclusive fora da janela de 24 h.',
  pendente: 'Em análise na Meta: enquanto não aprovar, nenhum toque com este modelo sai.',
  rejeitado: 'Recusado pela Meta: não sai. Corrija o texto e reenvie.',
  texto_livre: 'Não passa pela Meta: sai pela Evolution, por e-mail ou pela voz da IA.',
};

export const BOTAO_KIND_LABEL: Readonly<Record<ButtonKind, string>> = {
  link: 'Ir para o link (checkout)',
  reply: 'Resposta (cai com a vendedora)',
};

// -----------------------------------------------------------------------------
// Variaveis
// -----------------------------------------------------------------------------

export interface VariavelDisponivel {
  readonly name: string;
  readonly label: string;
  readonly exemplo: string;
}

/** As que o motor substitui hoje. Inserir outra gera texto que sai com chaves. */
export const VARIAVEIS: readonly VariavelDisponivel[] = [
  { name: 'primeiro_nome', label: 'Primeiro nome', exemplo: 'Mariana' },
  { name: 'produto', label: 'Produto', exemplo: 'Inglês Intensivo 12 meses' },
  { name: 'plano', label: 'Plano', exemplo: 'Clube de Prática (mensal)' },
  { name: 'link', label: 'Link do checkout', exemplo: 'https://pague.exemplo/abc123' },
  { name: 'valor', label: 'Valor', exemplo: 'R$ 497,00' },
  { name: 'vendedora', label: 'Vendedora (só com quem respondeu)', exemplo: 'Juliana' },
];

export function variavelPorNome(nome: string): VariavelDisponivel | null {
  return VARIAVEIS.find((v) => v.name === nome) ?? null;
}

const VARIAVEL_REGEX = /\{([a-z_]+)\}/g;

/** Nomes (sem chaves) na ordem em que aparecem, sem repetir. `{{1}}` nao casa (tem chave dupla). */
export function extrairVariaveis(...textos: readonly (string | undefined | null)[]): readonly string[] {
  const vistas = new Set<string>();
  const ordem: string[] = [];
  for (const texto of textos) {
    if (texto === undefined || texto === null) {
      continue;
    }
    for (const match of texto.matchAll(VARIAVEL_REGEX)) {
      const inicio = match.index ?? 0;
      // `{{nome}}` nao e variavel nomeada valida: a checagem acusa; aqui so nao conta.
      if (texto.charAt(inicio - 1) === '{') {
        continue;
      }
      const nome = match[1];
      if (nome !== undefined && !vistas.has(nome)) {
        vistas.add(nome);
        ordem.push(nome);
      }
    }
  }
  return ordem;
}

// -----------------------------------------------------------------------------
// Limites
// -----------------------------------------------------------------------------

export const LIMITE_BOTOES = 2;
export const LIMITE_ROTULO_BOTAO = 20;
export const LIMITE_CORPO_WHATSAPP = 1_024;
export const LIMITE_RODAPE = 60;
export const LIMITE_NOME = 64;
export const LIMITE_PRE_CABECALHO = 110;

const SLUG_REGEX = /^[a-z0-9_]+$/;

/** `null` quando valido; senao, a frase para o campo. `existentes` compara sem o proprio nome original. */
export function validarNome(nome: string, existentes: readonly string[]): string | null {
  if (nome.length === 0) {
    return 'Dê um nome ao modelo.';
  }
  if (nome.length < 3) {
    return 'Use ao menos 3 caracteres.';
  }
  if (nome.length > LIMITE_NOME) {
    return `Use até ${LIMITE_NOME} caracteres.`;
  }
  if (!SLUG_REGEX.test(nome)) {
    return 'Só minúsculas, números e underscore (ex.: carrinho_t1h_2botoes). A Meta recusa espaço, acento e hífen no nome.';
  }
  if (existentes.includes(nome)) {
    return 'Já existe um modelo com esse nome.';
  }
  return null;
}

/** Nome livre para uma copia: `_copia`, depois `_copia2`, `_copia3`... sempre dentro do limite. */
export function nomeDaCopia(original: string, existentes: readonly string[]): string {
  const comSufixo = (sufixo: string): string => `${original.slice(0, LIMITE_NOME - sufixo.length)}${sufixo}`;
  const base = comSufixo('_copia');
  if (!existentes.includes(base)) {
    return base;
  }
  for (let n = 2; n < 100; n += 1) {
    const candidato = comSufixo(`_copia${n}`);
    if (!existentes.includes(candidato)) {
      return candidato;
    }
  }
  return comSufixo('_copia99');
}

// -----------------------------------------------------------------------------
// Medidas por canal
// -----------------------------------------------------------------------------

export type ChaveMedida = keyof Omit<TemplatePerformance, 'recoveredCents'>;

/** Rotulo de cada medida no canal: voz liga e e atendida; e-mail e aberto; WhatsApp e lido. */
export const MEDIDA_LABEL: Readonly<Record<TemplateChannel, Readonly<Record<ChaveMedida, string>>>> = {
  whatsapp_oficial: {
    sent: 'receberam a mensagem',
    delivered: 'chegaram no aparelho',
    opened: 'leram',
    clicked: 'clicaram no botão de link',
    replied: 'responderam → vendedora',
    paid: 'pagaram (crédito neste toque)',
    paidViaSeller: 'fechados pela vendedora',
  },
  whatsapp_evolution: {
    sent: 'receberam a mensagem',
    delivered: 'chegaram no aparelho',
    opened: 'leram',
    clicked: 'clicaram no link do texto',
    replied: 'responderam → vendedora',
    paid: 'pagaram (crédito neste toque)',
    paidViaSeller: 'fechados pela vendedora',
  },
  email: {
    sent: 'e-mails enviados',
    delivered: 'entregues na caixa',
    opened: 'abriram o e-mail',
    clicked: 'clicaram no botão',
    replied: 'responderam o e-mail → comercial',
    paid: 'pagaram (crédito neste toque)',
    paidViaSeller: 'fechados pela vendedora',
  },
  voice: {
    sent: 'ligações feitas',
    delivered: 'atenderam a IA',
    opened: 'leram',
    clicked: 'clicaram',
    replied: 'pediram uma pessoa → vendedora',
    paid: 'pagaram (crédito nesta ligação)',
    paidViaSeller: 'fechados pela vendedora',
  },
};

/** Nome curto da medida de volume, para coluna de tabela e cartao. */
export const VOLUME_CURTO: Readonly<Record<TemplateChannel, string>> = {
  whatsapp_oficial: 'envios',
  whatsapp_evolution: 'envios',
  email: 'e-mails',
  voice: 'ligações',
};

// -----------------------------------------------------------------------------
// Ordenacao e visualizacao
// -----------------------------------------------------------------------------

export type ColunaOrdem = 'atualizado' | 'nome' | 'enviados' | 'respostas' | 'pagaram' | 'taxa_pagamento' | 'recuperado';

export const COLUNAS_ORDEM: readonly ColunaOrdem[] = ['pagaram', 'taxa_pagamento', 'recuperado', 'enviados', 'respostas', 'atualizado', 'nome'];

export const ORDEM_LABEL: Readonly<Record<ColunaOrdem, string>> = {
  atualizado: 'Atualizados por último',
  nome: 'Nome (A–Z)',
  enviados: 'Mais enviados',
  respostas: 'Mais respostas',
  pagaram: 'Mais pagamentos',
  taxa_pagamento: 'Maior taxa de pagamento',
  recuperado: 'Mais recuperado (R$)',
};

export type Visualizacao = 'cartoes' | 'tabela';

/** Quantos itens a grade mostra por vez: 3 linhas de 3 cartoes; a tabela, 15 linhas. */
export const PASSO_CARTOES = 9;
export const PASSO_TABELA = 15;

// -----------------------------------------------------------------------------
// Texto auxiliar
// -----------------------------------------------------------------------------

export function contar(n: number, singular: string, plural: string): string {
  return `${formatCount(n)} ${n === 1 ? singular : plural}`;
}

/**
 * Marcas combinantes (U+0300..U+036F): o que sobra do acento depois do NFD.
 * Montada por codigo, e nao como literal, para o arquivo seguir ASCII no
 * codigo e nao depender de escape `\u` que ferramentas de edicao decodificam.
 */
const ACENTOS = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g');

/** Minusculas e sem acento, para a busca casar "ultimo" com "Último". */
export function normalizarBusca(texto: string): string {
  return texto.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}
